import { SMSStateItem, SMS_STORE_ACTION_TYPE } from 'store/sms/sms.type';
import BaseNode, { BaseNodeProps, FlowNodeMenuItem } from 'components/FlowBuilder/FlowNodes/BaseNode/BaseNode';
import React, { useCallback, useMemo } from 'react';
import IconSMS from 'pages/FlowBuilder/assets/icons/IconSMS';
import { useAppSelector as useSelector, useThunkDispatch as useDispatch } from 'hooks/reduxHooks';
import IconDiscardChanges from 'components/Shared/Icons/IconDiscardChanges';
import useConfirmModal from 'hooks/useConfirmModal';
import { ConfirmationDialogProps } from 'components/Shared/shared.types';
import OptionalNodeWrapper from '../OptionalNodeWrapper/OptionalNodeWrapper';
import useJourneyStatus from 'hooks/useJourneyStatus';
import { getDefaultLanguage } from 'helpers/templated-experience.helper';
import { useFlowBuilderContext } from 'pages/FlowBuilder/context/flow-builder.context';
import { actionUsesOptionalWrapper } from 'pages/FlowBuilder/constants/flow-node-layout';
import { validateStepAction } from 'validators/ExperienceCanvas/journey.validator';
import { FlowNodeTypes } from '../node.types';
import { TOUCHPOINT_STATUSES } from 'components/ExperienceCanvas/types';
import { ERROR_TYPES } from 'pages/ExperienceCanvas/types';
import { getTooltipIcon } from 'pages/FlowBuilder/helpers/flow-builder.helpers';
import { FlightTooltip } from '@flybits/design-system';
import { JourneyComponentTypes } from 'pages/FlowBuilder/types/flow-builder.types';

import './SMSNode.scss';

const MAIN_CLASS = 'sms-node';
const CLASSES = {
  LABEL: `${MAIN_CLASS}__label`,
  LABEL_TOOLTIP: `${MAIN_CLASS}__label__tooltip`,
  LABEL_DISABLED: `${MAIN_CLASS}__label--disabled`,
  LABEL_TEXT: `${MAIN_CLASS}__label__text`,
  LABEL_NAME: `${MAIN_CLASS}__label__name`,
  LABEL_BUTTON: `${MAIN_CLASS}__label__button`,
};

type SMSNodeProps = {
  data: {
    sms: SMSStateItem;
    stepIndex: number;
  };
};

function SMSNode({ data: { sms, stepIndex } }: SMSNodeProps) {
  const { isJourneyLocked } = useJourneyStatus();
  const dependencyGraph = useSelector((state) => state.te.journey.dependencyGraph);
  const {
    sidePanels: { sms: smsSidePanel },
    modals: {
      rename: { setData: setRenameModalData },
    },
  } = useFlowBuilderContext();
  const dispatch = useDispatch();

  const { errorStatus, errorMessage } = useSelector((state) => {
    const { errorStatus, errors } = validateStepAction(state, stepIndex, FlowNodeTypes.SMS, sms.refId);
    // currently only revealing the first error on a node.
    const errorMessage = errors[0]?.err ?? '';

    if (errorStatus === '') {
      return { errorStatus: TOUCHPOINT_STATUSES.COMPLETE, errorMessage };
    } else if (errorStatus === ERROR_TYPES.INCOMPLETE) {
      return { errorStatus: TOUCHPOINT_STATUSES.INCOMPLETE, errorMessage };
    } else {
      return { errorStatus: TOUCHPOINT_STATUSES.ERROR, errorMessage };
    }
  });

  const isOptional = sms.isOptional;
  const isOptionalSMSEnabled = !!sms.enableOptionalNode;

  const language = useSelector((state) => getDefaultLanguage(state.settings.languages));
  const isConfigured = !sms.isReset && !!sms.payload?.template?.[language]?.journeySMSTemplateId;

  const handleSetupSMS = useCallback(() => {
    smsSidePanel.setStepIndex(stepIndex);
    smsSidePanel.setSMSContext({
      isOpen: true,
      isViewOnly: isJourneyLocked,
      sms,
    });
  }, [sms, smsSidePanel, stepIndex, isJourneyLocked]);

  const handleRenameSMS = useCallback(() => {
    setRenameModalData({
      entity: sms,
      entityType: JourneyComponentTypes.SMS,
      entityNodeName: sms.name,
      stepIndex,
    });
  }, [sms, setRenameModalData, stepIndex]);

  const onConfirmClearSelection = useCallback(() => {
    dispatch({
      type: SMS_STORE_ACTION_TYPE.RESET_SMS,
      payload: { refId: sms.refId },
    });
    dispatch({
      type: 'SHOW_SNACKBAR',
      payload: { content: 'The SMS template selection has been cleared.', type: 'info' },
    });
  }, [dispatch, sms.refId]);

  const confirmClearDialogProps: ConfirmationDialogProps = useMemo(
    () => ({
      icon: <IconDiscardChanges />,
      title: 'Are you sure you want to clear your selection?',
      description: 'This will clear the SMS template selection made.',
      primaryAction: {
        value: 'Yes, confirm',
        onClickHandler: onConfirmClearSelection,
      },
      secondaryAction: {
        value: 'Cancel',
      },
    }),
    [onConfirmClearSelection],
  );

  const [ClearSelectionConfirmModal, showClearSelectionConfirmModal] = useConfirmModal(confirmClearDialogProps);

  const handleClearSelection = useCallback(() => {
    showClearSelectionConfirmModal();
  }, [showClearSelectionConfirmModal]);

  const baseNodeProps = useMemo<Omit<BaseNodeProps, 'children'>>(() => {
    const menuItems: FlowNodeMenuItem[] = [];
    if (isConfigured) {
      if (isJourneyLocked) {
        menuItems.push({
          icon: 'eyeOpenOutline',
          label: 'View SMS',
          onClick: handleSetupSMS,
          iconRight: getTooltipIcon(errorStatus),
          iconRightTooltip: errorMessage,
        });
      } else {
        menuItems.push(
          {
            icon: 'editOutline',
            label: 'Edit SMS',
            onClick: handleSetupSMS,
            iconRight: getTooltipIcon(errorStatus),
            iconRightTooltip: errorMessage,
          },
          {
            icon: 'textBoxWithCorners',
            label: 'Rename',
            onClick: handleRenameSMS,
          },
          {
            icon: 'crossCircleOutline',
            label: 'Clear Selection',
            onClick: handleClearSelection,
          },
        );
      }
    }

    return {
      className: `${MAIN_CLASS} ${isConfigured ? '' : `${MAIN_CLASS}--unconfigured`}`,
      Icon: IconSMS,
      menuItems,
    };
  }, [errorMessage, errorStatus, handleSetupSMS, isConfigured, handleRenameSMS, isJourneyLocked, handleClearSelection]);

  const renderSMSNode = () => (
    <div
      className={`${CLASSES.LABEL} ${showOptionalNodeWrapper && !isOptionalSMSEnabled ? CLASSES.LABEL_DISABLED : ''}`}
    >
      <div className={CLASSES.LABEL_TEXT}>Send an SMS</div>
      {isConfigured ? (
        <FlightTooltip description={sms.name || 'SMS Template'} className={CLASSES.LABEL_TOOLTIP}>
          <div className={CLASSES.LABEL_NAME}>{sms.name || 'SMS Template'}</div>
        </FlightTooltip>
      ) : (
        <button className={CLASSES.LABEL_BUTTON} onClick={handleSetupSMS}>
          <span>+</span>
          Set up SMS
        </button>
      )}
      <ClearSelectionConfirmModal />
    </div>
  );

  const showOptionalNodeWrapper = actionUsesOptionalWrapper(sms.refId, isOptional, {
    isJourneyLocked,
    dependencyGraph,
  });

  return showOptionalNodeWrapper ? (
    <OptionalNodeWrapper isEnabled={isOptionalSMSEnabled} onToggle={() => null}>
      <BaseNode {...baseNodeProps} isOptional isDisabled={!isOptionalSMSEnabled} status={errorStatus}>
        {renderSMSNode()}
      </BaseNode>
    </OptionalNodeWrapper>
  ) : (
    <BaseNode {...baseNodeProps} status={errorStatus}>
      {renderSMSNode()}
    </BaseNode>
  );
}

export default SMSNode;
